import queryString from 'query-string'
import {pickBy, isEqual, omit, parseInt} from 'lodash'
import {Location} from 'history'
import moment from 'moment'
import {Schema} from 'features/employees/SearchForm'
import {EmployeesQueryVariables, SortColumn, SortOrder} from './query'

export interface SearchParams {
  q?: string
  employment_status?: 'all' | 'employed' | 'vacation' | 'unemployed' | 'join'
  office_ids?: string[]
  group_ids?: string[]
  position_ids?: string[]
  occupation_ids?: string[]
  employment_type_ids?: string[]
  age_from?: string
  age_to?: string
  age_base_date?: string
  joined_at_start?: string
  joined_at_end?: string
  former_name?: string
  birthday_start?: string
  birthday_end?: string
  genders?: string[]
  countries?: string[]
  prefecture_ids?: string[]
  national_types?: string[]
  nation_ids?: string[]
  visa_type_ids?: string[]
  retired_at_start?: string
  retired_at_end?: string
  retire_types?: string[]
  has_dependents?: string[]
  marital_statuses?: string[]
  remuneration_types?: string[]
  handicap_types?: string[]
  widow_types?: string[]
  working_student_types?: string[]
  tag_ids?: string[]
  skills?: string[]
  qualifications?: string[]
  language_ids?: string // 複数形だが今のところ単一選択
  language_level_ids?: string // 複数形だが今のところ単一選択
  languages_qualification_name?: string
  languages_score_from?: string
  languages_score_to?: string
  projects?: string[]
  _page?: string
  limit?: string
  sort_type?: string
  _added_fields?: string[]
}

export const defaultSearchParams = {
  employment_status: 'employed',
  _page: '1',
  sort_type: 'staff_code__asc',
} satisfies SearchParams

export const getSearchParamsFromLocation = (location: Location): SearchParams => {
  return {
    ...defaultSearchParams,
    ...queryString.parse(location.search, {arrayFormat: 'bracket'}),
  }
}

export const convertSearchParamsToQueryString = (params: SearchParams): string =>
  queryString.stringify(params, {arrayFormat: 'bracket'})

export const convertSearchParamsToForm = (params: SearchParams): Schema => {
  return {
    q: params.q || '',
    status: params.employment_status || 'all',
    offices: params.office_ids || [],
    groups: params.group_ids || [],
    positions: params.position_ids || [],
    occupations: params.occupation_ids || [],
    employmentTypes: params.employment_type_ids || [],
    ageFrom: params.age_from ? parseInt(params.age_from, 10) : undefined,
    ageTo: params.age_to ? parseInt(params.age_to, 10) : undefined,
    ageBaseDate: params.age_base_date || moment().format('YYYY/MM/DD'),
    joinedAtStart: params.joined_at_start || '',
    joinedAtEnd: params.joined_at_end || '',
    formerName: params.former_name || '',
    birthdayStart: params.birthday_start || '',
    birthdayEnd: params.birthday_end || '',
    genders: params.genders || [],
    countries: params.countries || [],
    prefectureIds: params.prefecture_ids || [],
    nationalTypes: params.national_types || [],
    nationIds: params.nation_ids || [],
    visaTypeIds: params.visa_type_ids || [],
    retiredAtEnd: params.retired_at_end || '',
    retiredAtStart: params.retired_at_start || '',
    retireTypes: params.retire_types || [],
    hasDependents: params.has_dependents || [],
    maritalStatuses: params.marital_statuses || [],
    remunerationTypes: params.remuneration_types || [],
    handicapTypes: params.handicap_types || [],
    widowTypes: params.widow_types || [],
    workingStudentTypes: params.working_student_types || [],
    tags: params.tag_ids || [],
    // FIXME: skills, qualifications, projects はスペース区切りのはずだが、旧SearchFormではカンマ区切りでフォームにセットされていた https://github.com/d-o-n-u-t-s/roumu-server/issues/10132
    skills: params.skills?.join(',') || '',
    qualifications: params.qualifications?.join(',') || '',
    languages: params.language_ids || '',
    languageLevels: params.language_level_ids || '',
    languagesQualificationName: params.languages_qualification_name || '',
    languagesScoreFrom: params.languages_score_from ? parseInt(params.languages_score_from, 10) : undefined,
    languagesScoreTo: params.languages_score_to ? parseInt(params.languages_score_to, 10) : undefined,
    projects: params.projects?.join(',') || '',
    page: params._page || '1',
    limit: params.limit || '100',
    sortType: params.sort_type || defaultSearchParams.sort_type,
    addedFields: params._added_fields || [],
  }
}

export const convertFormToSearchParams = (form: Schema): SearchParams => {
  return pickBy({
    q: form.q,
    employment_status: form.status,
    office_ids: form.offices,
    group_ids: form.groups,
    position_ids: form.positions,
    occupation_ids: form.occupations,
    employment_type_ids: form.employmentTypes,
    age_from: form.ageFrom ? String(form.ageFrom) : undefined,
    age_to: form.ageTo ? String(form.ageTo) : undefined,
    age_base_date: form.ageBaseDate,
    joined_at_start: form.joinedAtStart,
    joined_at_end: form.joinedAtEnd,
    former_name: form.formerName,
    birthday_start: form.birthdayStart,
    birthday_end: form.birthdayEnd,
    genders: form.genders,
    countries: form.countries,
    prefecture_ids: form.prefectureIds,
    national_types: form.nationalTypes,
    nation_ids: form.nationIds,
    visa_type_ids: form.visaTypeIds,
    retired_at_start: form.retiredAtStart,
    retired_at_end: form.retiredAtEnd,
    retire_types: form.retireTypes,
    has_dependents: form.hasDependents,
    marital_statuses: form.maritalStatuses,
    remuneration_types: form.remunerationTypes,
    handicap_types: form.handicapTypes,
    widow_types: form.widowTypes,
    working_student_types: form.workingStudentTypes,
    tag_ids: form.tags,
    skills: form.skills ? form.skills.split(/,|\s/) : undefined,
    qualifications: form.qualifications ? form.qualifications.split(/,|\s/) : undefined,
    language_ids: form.languages ?? undefined,
    language_level_ids: form.languageLevels ?? undefined,
    languages_qualification_name: form.languagesQualificationName,
    languages_score_from: form.languagesScoreFrom ? String(form.languagesScoreFrom) : undefined,
    languages_score_to: form.languagesScoreTo ? String(form.languagesScoreTo) : undefined,
    projects: form.projects ? form.projects.split(/,|\s/) : undefined,
    sort_type: form.sortType,
    _added_fields: form.addedFields?.length ? form.addedFields : undefined,
    _page: form.page,
    limit: undefined, // limitはSearchParamsに含めない
  } satisfies SearchParams)
}

const nullableList = (list: string[] | undefined): (string | null)[] | undefined =>
  list?.map((v) => (v === 'null' ? null : v))

export const convertSearchParamsToGraphQL = (params: SearchParams, procedureType: string): EmployeesQueryVariables => {
  const [sortColumn, sortOrder] = (params.sort_type || defaultSearchParams.sort_type).split('__')
  return {
    page: Number(params._page) || 1,
    limit: Number(params.limit) || 100,
    search: {
      ...(params.employment_status !== 'all' && {employmentStatus: params.employment_status}),
      q: params.q,
      officeIds: params.office_ids,
      groupIds: params.group_ids,
      positionIds: params.position_ids,
      occupationIds: params.occupation_ids,
      employmentTypeIds: params.employment_type_ids,
      ageFrom: params.age_from ? Number(params.age_from) : undefined,
      ageTo: params.age_to ? Number(params.age_to) : undefined,
      ageBaseDate: params.age_base_date || moment().format('YYYY/MM/DD'),
      joinedAtStart: params.joined_at_start,
      joinedAtEnd: params.joined_at_end,
      formerName: params.former_name,
      birthdayStart: params.birthday_start,
      birthdayEnd: params.birthday_end,
      genders: nullableList(params.genders),
      countries: params.countries,
      prefectureIds: params.prefecture_ids,
      nationalTypes: nullableList(params.national_types),
      nationIds: params.nation_ids,
      visaTypeIds: params.visa_type_ids,
      retiredAtStart: params.retired_at_start,
      retiredAtEnd: params.retired_at_end,
      retireTypes: nullableList(params.retire_types),
      hasDependents: params.has_dependents?.map((v) => v === 'true'),
      maritalStatuses: nullableList(params.marital_statuses),
      remunerationTypes: nullableList(params.remuneration_types),
      handicapTypes: nullableList(params.handicap_types),
      widowTypes: nullableList(params.widow_types),
      workingStudentTypes: nullableList(params.working_student_types),
      tagIds: params.tag_ids,
      skills: params.skills,
      qualifications: params.qualifications,
      languageIds: params.language_ids ? [params.language_ids] : undefined,
      languageLevelIds: params.language_level_ids ? [params.language_level_ids] : undefined,
      languagesQualificationName: params.languages_qualification_name,
      languagesScoreFrom: params.languages_score_from ? Number(params.languages_score_from) : undefined,
      languagesScoreTo: params.languages_score_to ? Number(params.languages_score_to) : undefined,
      projects: params.projects,
      applicableProcedure: procedureType,
      sortColumn: sortColumn as SortColumn,
      sortOrder: sortOrder as SortOrder,
    },
  }
}

const ignoreFields = ['_added_fields', 'age_base_date']

export const hasConditions = (form: Schema): boolean => {
  return !isEqual(
    pickBy(omit(convertFormToSearchParams(form), ignoreFields), (v) => (Array.isArray(v) ? v.length > 0 : v)),
    defaultSearchParams
  )
}
